"use client";

import { FC, useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";

import { OfficeRecord } from "@graphql/types/graphql";
import { convertOfficesToGeoJson } from "utils";

import { OfficeTeaser } from "components/molecules/Teaser/OfficeTeaser";
import { MapboxMapDynamic } from "components/molecules/MapboxMap/MapboxMapDynamic";
import { ToggleView } from "components/molecules/ToggleView/ToggleView";
import { Grid } from "components/organisms/Grid/Grid";
import { Loader } from "components/atoms/Loader/Loader";
import { NoResults } from "components/atoms/NoResults/NoResults";

import { SearchPaginationContainer as SearchPagination } from "../SearchPaginationContainer";
import s from "./OfficesResults.module.scss";

interface Props {
  offices: OfficeRecord[];
  mapOffices: OfficeRecord[];
  loading: boolean;
  reset: () => void;
}

export const OfficesResults: FC<Props> = ({ offices = [], mapOffices = [], loading, reset }) => {
  const t = useTranslations("search");
  const officesGeoJSON = useMemo(() => convertOfficesToGeoJson(mapOffices), [mapOffices]);

  const renderListView = useCallback(
    () => (
      <>
        {loading && <Loader />}
        <Grid columns="3">
          {offices.map((office) => (
            <OfficeTeaser office={office} key={office.id} />
          ))}
        </Grid>
        <SearchPagination />
      </>
    ),
    [loading, offices],
  );

  const renderMapView = useCallback(
    () => (
      <>
        <MapboxMapDynamic source={officesGeoJSON} />
        <Grid columns="3">
          {offices.map((office) => (
            <OfficeTeaser office={office} key={office.id} />
          ))}
        </Grid>
        <SearchPagination />
      </>
    ),
    [officesGeoJSON, loading, offices],
  );

  if (!loading && offices.length === 0) {
    return <NoResults copy={`${t("no-results-offices")}.`} onClearClick={() => reset()} />;
  }

  return (
    <div className={s.OfficesResults}>
      <ToggleView view1={renderListView()} view2={renderMapView()} />
    </div>
  );
};
